import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, mergeProps as _mergeProps, createTextVNode as _createTextVNode, withCtx as _withCtx, createSlots as _createSlots, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, isRef as _isRef, Fragment as _Fragment, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "flex flex-col items-center gap-1" };
const _hoisted_2 = ["id"];
const _hoisted_3 = ["id"];
const _hoisted_4 = { class: "text-sm font-normal leading-5 text-center text-(--text-text-heading) dark:text-(--text-text-white-default)" };
const _hoisted_5 = { class: "text-sm font-normal leading-5 text-center text-(--text-text-heading) dark:text-(--text-text-white-default)" };
const _hoisted_6 = {
    class: "flex flex-col gap-5 md:flex-row md:gap-8 md:min-h-125.5",
    "data-testid": "contact-create-modal"
};
const _hoisted_7 = { class: "flex flex-col flex-1 min-w-0 gap-0.5" };
const _hoisted_8 = { class: "lg:w-64" };
const _hoisted_9 = { class: "flex flex-col lg:flex-row gap-x-5 gap-y-0.5" };
const _hoisted_10 = { class: "flex flex-col lg:flex-row gap-x-5 gap-y-0.5" };
const _hoisted_11 = ["aria-label"];
const _hoisted_12 = { class: "flex flex-col lg:flex-row gap-x-5 gap-y-0.5" };
const _hoisted_13 = { class: "flex flex-col lg:flex-row gap-x-5 gap-y-0.5" };
const _hoisted_14 = {
    key: 3,
    class: "rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center gap-2"
};
const _hoisted_15 = { class: "w-full md:w-92.5 md:shrink-0 md:relative" };
const _hoisted_16 = { class: "md:absolute md:inset-0 flex flex-col" };
import { computed, ref, useId, watch, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { AutoComplete, Button, Checkbox, Modal, PhoneInput, Popover, SelectInput, TextInput, toE164 } from '@eudonet/opal';
import { buildAutoCompleteLabels } from '@/shared/composables/autoCompleteLabels';
import { useCreateContactForm } from '../../composables/useCreateContactForm';
import { useContactDeduplication, DEDUP_PANEL_MAX } from '../../composables/useContactDeduplication';
import { useContactPreview } from '../../composables/useContactPreview';
import { createNewContact, addContactAddress } from '../../infrastructure/adapters/contactService';
import { mapErrorCodeToField } from '../utils/contactErrorCodeMap';
import { useAppToast } from '@/shared/composables/useAppToast';
import { useOrganisationSearch } from '@/modules/organisation/public';
import { useSharedEmailSetting } from '@/modules/workspace/public';
import { useAddressForm } from '@/modules/organisation/composables/useAddressForm';
import { parseAddressQuery } from '@/modules/organisation/domain/address-utils';
import { useWorkspaceCountry } from '@/shared/composables/useWorkspaceCountry';
import { toAddressLayout, withStreetLineOnly } from '@/shared/utils/addressFieldConfig';
import { logger } from '@/infrastructure/services/LoggerService';
import ContactDedupPanel from './ContactDedupPanel.vue';
import ContactPreviewDrawer from './ContactPreviewDrawer.vue';
import AddressDetailsModal from '@/modules/organisation/presentation/components/AddressDetailsModal.vue';
import CreateOrganisationModal from '@/modules/organisation/presentation/components/CreateOrganisationModal.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'CreateContactModal',
    props: /*@__PURE__*/ _mergeModels({
        redirectOnCreate: { type: Boolean, default: true },
        initialOrganisationId: { default: undefined },
        initialOrganisationLabel: { default: undefined },
        forceProfessionalType: { type: Boolean, default: false },
        createDefaultRelation: { type: Boolean, default: true }
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["created"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const open = _useModel(__props, "modelValue");
        const { sharedEmailsEnabled, sharedEmailSettingPending, loadSharedEmailSetting, onEmailWriteRejected } = useSharedEmailSetting();
        watch(open, (isOpen) => { if (isOpen)
            void loadSharedEmailSetting(); }, { immediate: true });
        const props = __props;
        const emit = __emit;
        const router = useRouter();
        const { t, locale } = useI18n();
        const { toastSuccess, toastError } = useAppToast();
        const titleId = useId();
        const descriptionId = useId();
        const { form, variantIndividual, variantPro, firstNameError, lastNameError, mainEmailError, contactTypeError, phoneError, mainPhoneValid, organisationIdError, positionError, onFirstNameBlur, onLastNameBlur, onMainEmailBlur, onOrganisationBlur, onPositionBlur, onPhoneValidationChange, validateRequired, resetForm, } = useCreateContactForm();
        const { suggestions: organisationSuggestions, isLoading: organisationsLoading, search: searchOrgs, clear: clearOrgSearch } = useOrganisationSearch();
        // OP-6164: inline organisation creation from the Professional organisation field.
        // `lastOrgQuery` carries the term the user typed so it pre-fills the org modal's
        // legal name; `createOrgMounted` lazily mounts the nested modal on first open.
        const showCreateOrg = ref(false);
        const createOrgMounted = ref(false);
        const lastOrgQuery = ref('');
        watch(showCreateOrg, (v) => {
            if (v)
                createOrgMounted.value = true;
        });
        function onOrgSearch(query) {
            lastOrgQuery.value = query;
            searchOrgs(query);
        }
        function openCreateOrg() {
            showCreateOrg.value = true;
        }
        function onOrgCreated(organisationId, name) {
            showCreateOrg.value = false;
            variantPro.organisationId = organisationId;
            // Inject the new org as the current option so the AutoComplete renders its name;
            // mutating variantPro.organisationId re-runs the existing dedup watch.
            organisationSuggestions.value = [{ value: organisationId, label: name || organisationId }];
        }
        const { address, city, postalCode, addressDetails, addressDetailsSaved, addressSuggestions, addressLoading, showAddressDetailsModal, onAddressSearch, onAddressSelect, openAddressDetailsModal, saveAddressDetails, reset: resetAddress, } = useAddressForm(3);
        const workspaceCountry = useWorkspaceCountry();
        const showAddressDetailsLink = computed(() => address.value.trim().length > 0);
        // When the modal is opened with an initial organisation context, force Professional
        // type and pre-fill + lock the organisation field.
        const lockedOrgOption = computed(() => props.initialOrganisationId
            ? [{ value: props.initialOrganisationId, label: props.initialOrganisationLabel ?? props.initialOrganisationId }]
            : undefined);
        // Professional is forced both when an organisation is pre-selected+locked (initialOrganisationId)
        // and when the host only knows the contact must be a referent without a specific org yet
        // (forceProfessionalType) — the latter keeps the organisation field editable.
        const isProfessionalForced = computed(() => !!props.initialOrganisationId || props.forceProfessionalType);
        watchEffect(() => {
            if (!open.value)
                return;
            if (isProfessionalForced.value)
                form.contactType = 'Professional';
            if (props.initialOrganisationId)
                variantPro.organisationId = props.initialOrganisationId;
        });
        // Cache the chosen organisation's label at selection time. `organisationSuggestions` is
        // replaced on every re-search, so resolving the label lazily at submit can miss it (the
        // user may have typed again after selecting) and emit only the GUID. Resolving here — while
        // the selected option is still in the buffer (or lockedOrgOption) — keeps the label stable.
        const selectedOrgLabel = ref();
        watch(() => variantPro.organisationId, (id) => {
            if (!id) {
                selectedOrgLabel.value = undefined;
                return;
            }
            const opt = (lockedOrgOption.value ?? organisationSuggestions.value).find((o) => o.value === id);
            if (opt?.label)
                selectedOrgLabel.value = opt.label;
        }, { immediate: true });
        const { dedupState, dedupResults, dedupTitle, dedupHint, hasExactEmailMatch, runDeduplication, reset: resetDedup } = useContactDeduplication();
        const { showPreviewDrawer, previewContact, previewContactId, previewLoading, previewError, fetchPreview, retryPreview, reset: resetPreview } = useContactPreview();
        const previewName = ref();
        async function handlePreview(result) {
            previewName.value = result.fullName;
            showPreviewDrawer.value = true;
            await fetchPreview(result.id);
        }
        function handlePreviewGoTo() {
            router.push({ name: 'ContactDetail', params: { id: previewContactId.value } });
        }
        watch(() => [form.mainEmail, form.firstName, form.lastName, form.mainPhone, variantPro.organisationId, mainPhoneValid.value], ([email, firstName, lastName, phone, organisationId, phoneValid]) => {
            runDeduplication({
                email: email || undefined,
                firstName: firstName.trim() || undefined,
                lastName: lastName.trim() || undefined,
                // OP-7665: phone is PhoneInput's raw v-model (trunk 0 kept) — normalize to strict E.164
                // the same way handleSubmit does, so the dedup lookup key matches stored contacts.
                phone: toE164(phone.trim()) || undefined,
                organisationId: organisationId || undefined,
            }, { phoneValid });
        }, { immediate: false });
        // OP-5601 / OP-8215: the typed email already exists on another contact. The opt-in is offered
        // only when the workspace allows shared emails.
        const associatedPrincipalName = computed(() => dedupResults.value.find((r) => r.emailExactMatch)?.fullName ?? '');
        const sharedEmailOffered = computed(() => hasExactEmailMatch.value && sharedEmailsEnabled.value);
        // Once the user ticks "share this email", the dedup panel must stop saying "creation blocked"
        // and instead show the informational shared-email message — for BOTH contact types.
        const sharingAccepted = computed(() => form.sharedEmail && sharedEmailOffered.value);
        // An already-shared mailbox reads "creation allowed"; without the opt-in it blocks like any
        // duplicate. Reachable with the setting really on when its last read failed.
        const sharedDuplicateBlocked = computed(() => dedupState.value === 'duplicateFoundShared' && !sharedEmailsEnabled.value);
        // Until the session's first read of the setting settles, an exact match can be neither offered
        // for sharing nor reported as blocking: the panel keeps checking.
        const settingPendingOnMatch = computed(() => hasExactEmailMatch.value && sharedEmailSettingPending.value);
        const effectiveDedupState = computed(() => {
            if (sharingAccepted.value)
                return 'duplicateFoundShared';
            if (settingPendingOnMatch.value)
                return 'checking';
            return sharedDuplicateBlocked.value ? 'duplicateFound' : dedupState.value;
        });
        const effectiveDedupTitle = computed(() => {
            const count = dedupResults.value.length;
            if (sharingAccepted.value)
                return t('contact.create.dedup.duplicateFoundShared', { count });
            if (settingPendingOnMatch.value)
                return t('contact.create.dedup.title');
            return sharedDuplicateBlocked.value ? t('contact.create.dedup.duplicateFound', { count }) : dedupTitle.value;
        });
        const effectiveDedupHint = computed(() => {
            if (sharingAccepted.value)
                return t('contact.create.dedup.sharedEmailHint');
            if (settingPendingOnMatch.value)
                return t('contact.create.dedup.checking');
            return sharedDuplicateBlocked.value ? t('contact.create.dedup.duplicateHint') : dedupHint.value;
        });
        // The panel colours each row from `emailIsShared`: a blocked shared match must read as a plain duplicate.
        const effectiveDedupResults = computed(() => sharedDuplicateBlocked.value ? dedupResults.value.map((r) => ({ ...r, emailIsShared: false })) : dedupResults.value);
        // When the duplicate disappears (email edited to a unique value) the opt-in is no longer
        // meaningful — clear it so a stale tick can't slip through on the next exact match.
        // Watch dedupState alongside hasExactEmailMatch so we don't clear the checkbox during the
        // transient 'checking' phase (dedupResults is emptied before results arrive, which would
        // momentarily make hasExactEmailMatch false even when the same email still has a match).
        watch([hasExactEmailMatch, dedupState], ([has, state]) => {
            if (!has && state !== 'checking')
                form.sharedEmail = false;
        });
        watch(sharedEmailsEnabled, (enabled) => {
            if (!enabled)
                form.sharedEmail = false;
        });
        const salutationOptions = computed(() => [
            { value: 'Mr', label: t('contact.create.fields.salutation.options.mr') },
            { value: 'Mrs', label: t('contact.create.fields.salutation.options.mrs') },
            { value: 'Ms', label: t('contact.create.fields.salutation.options.ms') },
            { value: 'Dr', label: t('contact.create.fields.salutation.options.dr') },
            { value: 'Other', label: t('contact.create.fields.salutation.options.other') },
        ]);
        const contactTypeOptions = computed(() => [
            { value: 'Individual', label: t('contact.create.contactType.individual') },
            { value: 'Professional', label: t('contact.create.contactType.professional') },
        ]);
        const categoryOptions = computed(() => [
            { value: 'Member', label: t('contact.create.fields.category.options.member') },
            { value: 'Sympathizer', label: t('contact.create.fields.category.options.sympathizer') },
            { value: 'Donor', label: t('contact.create.fields.category.options.donor') },
            { value: 'Volunteer', label: t('contact.create.fields.category.options.volunteer') },
            { value: 'Other', label: t('contact.create.fields.category.options.other') },
        ]);
        const sourceOptions = computed(() => [
            { value: 'Web', label: t('contact.create.fields.source.options.web') },
            { value: 'Phone', label: t('contact.create.fields.source.options.phone') },
            { value: 'InPerson', label: t('contact.create.fields.source.options.inPerson') },
            { value: 'Event', label: t('contact.create.fields.source.options.event') },
            { value: 'Import', label: t('contact.create.fields.source.options.import') },
            { value: 'Other', label: t('contact.create.fields.source.options.other') },
        ]);
        const isSubmitEnabled = computed(() => {
            // Salutation / Category / Source are optional (OP-5641). Only name + type gate every contact;
            // a Professional additionally requires an organisation and a position (fonction).
            if (!form.firstName.trim() || !form.lastName.trim() || !form.contactType)
                return false;
            if (firstNameError.value || lastNameError.value)
                return false;
            if (form.contactType === 'Professional') {
                if (!variantPro.organisationId || !variantPro.position.trim())
                    return false;
            }
            if (mainEmailError.value || phoneError.value)
                return false;
            // OP-5601: an exact email match (shared OR not) blocks creation until the user explicitly
            // opts into sharing the mailbox — ticking the box, offered only while sharing is enabled,
            // re-enables the CTA. The BE still requires
            // the opt-in even for an already-shared mailbox, so this single gate covers both cases.
            if (dedupState.value === 'checking')
                return false;
            if (hasExactEmailMatch.value && !sharingAccepted.value)
                return false;
            return true;
        });
        const submitting = ref(false);
        const serverError = ref(null);
        const FIELD_ERROR_REFS = {
            contactTypeError,
            mainEmailError,
            phoneError,
            organisationIdError,
            positionError,
        };
        async function handleSubmit() {
            if (!validateRequired())
                return;
            submitting.value = true;
            serverError.value = null;
            try {
                const contactId = await createNewContact({
                    salutation: (form.salutation || undefined),
                    firstName: form.firstName.trim(),
                    lastName: form.lastName.trim(),
                    mainEmail: form.mainEmail || undefined,
                    type: form.contactType,
                    category: form.contactType === 'Individual' ? (variantIndividual.category || undefined) : undefined,
                    source: form.contactType === 'Individual' ? (variantIndividual.source || undefined) : undefined,
                    // OP-7665: form.mainPhone is PhoneInput's raw v-model (trunk 0 kept, per AC3) — normalize to
                    // strict E.164 on the wire, mirroring useAddPhoneForm/saveEncounterRequest, rather than
                    // relying on OPHUB's own NormalizeToE164 fallback.
                    mainPhone: toE164(form.mainPhone.trim()) || undefined,
                    organisationId: form.contactType === 'Professional' ? variantPro.organisationId || undefined : undefined,
                    position: form.contactType === 'Professional' ? variantPro.position || undefined : undefined,
                    sharedEmail: form.sharedEmail && sharedEmailsEnabled.value,
                    createDefaultRelation: props.createDefaultRelation,
                });
                if (form.contactType === 'Individual' && address.value.trim()) {
                    try {
                        const ad = addressDetails;
                        const saved = addressDetailsSaved.value;
                        const parsed = parseAddressQuery(address.value.trim());
                        const street = parsed.line1?.trim() || address.value.trim();
                        const f = toAddressLayout(saved ? ad : withStreetLineOnly(ad, street, workspaceCountry.value), workspaceCountry.value);
                        await addContactAddress(contactId, {
                            type: 'Home',
                            addressLine: f.line1 || undefined,
                            addressLine2: f.line2 || undefined,
                            addressLine3: f.line3 || undefined,
                            houseNumber: f.houseNumber || undefined,
                            houseNumberSuffix: f.houseNumberSuffix || undefined,
                            region: f.region || undefined,
                            postalCode: ad.postalCode || postalCode.value || undefined,
                            city: city.value || undefined,
                            // useAddressForm seeds it with the workspace default; the user's choice replaces it.
                            country: ad.country || undefined,
                            isPrimary: true,
                        });
                    }
                    catch (addrError) {
                        logger.error('Failed to create contact address', addrError);
                        serverError.value = t('contact.create.errors.serverError');
                        return;
                    }
                }
                const linkedOrg = form.contactType === 'Professional' && variantPro.organisationId
                    ? {
                        id: variantPro.organisationId,
                        // `selectedOrgLabel` is captured at selection time (survives re-search); fall back
                        // to the current buffer, then the locked label, so the host never renders the GUID.
                        label: selectedOrgLabel.value ??
                            (lockedOrgOption.value ?? organisationSuggestions.value).find((o) => o.value === variantPro.organisationId)?.label,
                    }
                    : undefined;
                emit('created', contactId, form.contactType === 'Professional' ? variantPro.position || undefined : undefined, { firstName: form.firstName.trim(), lastName: form.lastName.trim() }, linkedOrg);
                resetForm();
                resetAddress();
                open.value = false;
                toastSuccess('contact.create.saveSuccess');
                if (props.redirectOnCreate) {
                    router.push({ name: 'ContactDetail', params: { id: contactId } });
                }
            }
            catch (error) {
                onEmailWriteRejected(error);
                const data = error?.response?.data;
                if (data && typeof data === 'object' && 'errorCode' in data) {
                    const code = data.errorCode;
                    const mapping = mapErrorCodeToField(code);
                    if (mapping?.field) {
                        FIELD_ERROR_REFS[mapping.field].value = t(mapping.i18nKey);
                    }
                    else if (mapping) {
                        serverError.value = t(mapping.i18nKey);
                    }
                    else {
                        serverError.value = t('contact.create.errors.serverError');
                        toastError('contact.create.errors.serverError');
                    }
                }
                else {
                    serverError.value = t('contact.create.errors.serverError');
                    toastError('contact.create.errors.serverError');
                }
                logger.error('Failed to create contact', error);
            }
            finally {
                submitting.value = false;
            }
        }
        function handleCancel() {
            resetForm();
            resetDedup();
            resetPreview();
            previewName.value = undefined;
            resetAddress();
            lastOrgQuery.value = '';
            open.value = false;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(Modal), {
                    modelValue: open.value,
                    "onUpdate:modelValue": [
                        _cache[16] || (_cache[16] = ($event) => ((open).value = $event)),
                        _cache[17] || (_cache[17] = (v) => !v && handleCancel())
                    ],
                    size: "lg",
                    "show-close-button": true,
                    "show-header-border": false,
                    "show-footer-border": false,
                    "footer-position": "split",
                    "close-on-escape": !_unref(showAddressDetailsModal),
                    "aria-labelledby": _unref(titleId),
                    "aria-describedby": _unref(descriptionId)
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                            _createElementVNode("h2", {
                                id: _unref(titleId),
                                class: "text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)"
                            }, _toDisplayString(_ctx.$t('contact.create.title')), 9 /* TEXT, PROPS */, _hoisted_2),
                            _createElementVNode("div", {
                                id: _unref(descriptionId),
                                class: "flex flex-col items-center gap-0.5"
                            }, [
                                _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.$t('contact.create.subtitle')), 1 /* TEXT */),
                                _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t('contact.create.requiredFieldsHint')), 1 /* TEXT */)
                            ], 8 /* PROPS */, _hoisted_3)
                        ])
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            "data-testid": "contact-create-cancel",
                            label: _ctx.$t('contact.create.actions.cancel'),
                            onClick: handleCancel
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            "icon-left": "ri-check-line",
                            "data-testid": "contact-create-submit",
                            label: _ctx.$t('contact.create.actions.submit'),
                            disabled: !isSubmitEnabled.value || submitting.value,
                            loading: submitting.value,
                            onClick: handleSubmit
                        }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_6, [
                            _createElementVNode("div", _hoisted_7, [
                                _createElementVNode("div", _hoisted_8, [
                                    _createVNode(_unref(SelectInput), {
                                        modelValue: _unref(form).salutation,
                                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((_unref(form).salutation) = $event)),
                                        label: _ctx.$t('contact.create.fields.salutation.label'),
                                        placeholder: _ctx.$t('contact.create.fields.salutation.placeholder'),
                                        options: salutationOptions.value,
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "options"])
                                ]),
                                _createElementVNode("div", _hoisted_9, [
                                    _createVNode(_unref(TextInput), {
                                        modelValue: _unref(form).firstName,
                                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((_unref(form).firstName) = $event)),
                                        "data-testid": "contact-create-first-name",
                                        label: _ctx.$t('contact.create.fields.firstName'),
                                        error: _unref(firstNameError),
                                        required: true,
                                        "full-width": "",
                                        onBlur: _unref(onFirstNameBlur)
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "error", "onBlur"]),
                                    _createVNode(_unref(TextInput), {
                                        modelValue: _unref(form).lastName,
                                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((_unref(form).lastName) = $event)),
                                        "data-testid": "contact-create-last-name",
                                        label: _ctx.$t('contact.create.fields.lastName'),
                                        error: _unref(lastNameError),
                                        required: true,
                                        "full-width": "",
                                        onBlur: _unref(onLastNameBlur)
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "error", "onBlur"])
                                ]),
                                _createElementVNode("div", _hoisted_10, [
                                    _createVNode(_unref(TextInput), {
                                        modelValue: _unref(form).mainEmail,
                                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((_unref(form).mainEmail) = $event)),
                                        "data-testid": "contact-create-email",
                                        label: _ctx.$t('contact.create.fields.email'),
                                        error: _unref(mainEmailError),
                                        type: "email",
                                        "full-width": "",
                                        onBlur: _unref(onMainEmailBlur)
                                    }, _createSlots({ _: 2 /* DYNAMIC */ }, [
                                        (sharingAccepted.value)
                                            ? {
                                                name: "iconRight",
                                                fn: _withCtx(() => [
                                                    _createVNode(_unref(Popover), {
                                                        position: "top",
                                                        header: _ctx.$t('contact.create.sharedEmail.tooltipTitle')
                                                    }, {
                                                        trigger: _withCtx(({ triggerProps }) => [
                                                            _createElementVNode("button", _mergeProps(triggerProps, {
                                                                type: "button",
                                                                class: "flex items-center text-(--icons-icon-success)",
                                                                "aria-label": _ctx.$t('contact.create.sharedEmail.tooltipTitle')
                                                            }), [...(_cache[21] || (_cache[21] = [
                                                                    _createElementVNode("i", {
                                                                        class: "ri-group-2-line text-base",
                                                                        "aria-hidden": "true"
                                                                    }, null, -1 /* CACHED */)
                                                                ]))], 16 /* FULL_PROPS */, _hoisted_11)
                                                        ]),
                                                        default: _withCtx(() => [
                                                            _createTextVNode(" " + _toDisplayString(_ctx.$t('contact.create.sharedEmail.tooltipBody', { name: associatedPrincipalName.value })), 1 /* TEXT */)
                                                        ]),
                                                        _: 1 /* STABLE */
                                                    }, 8 /* PROPS */, ["header"])
                                                ]),
                                                key: "0"
                                            }
                                            : undefined
                                    ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["modelValue", "label", "error", "onBlur"]),
                                    _createVNode(_unref(SelectInput), {
                                        modelValue: _unref(form).contactType,
                                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((_unref(form).contactType) = $event)),
                                        "data-testid": "contact-create-type",
                                        label: _ctx.$t('contact.create.fields.contactType'),
                                        options: contactTypeOptions.value,
                                        error: _unref(contactTypeError),
                                        required: true,
                                        disabled: isProfessionalForced.value,
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "options", "error", "disabled"])
                                ]),
                                (sharedEmailOffered.value)
                                    ? (_openBlock(), _createElementBlock("div", {
                                        key: 0,
                                        class: _normalizeClass([
                                            'flex items-center gap-2.5 p-4 rounded-xl border w-full mb-5',
                                            _unref(form).sharedEmail
                                                ? 'bg-(--background-background-success) border-(--border-border-success)'
                                                : 'bg-(--background-background-warning) border-(--border-border-warning-subtle)',
                                        ])
                                    }, [
                                        _createVNode(_unref(Checkbox), {
                                            modelValue: _unref(form).sharedEmail,
                                            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((_unref(form).sharedEmail) = $event)),
                                            label: _ctx.$t('contact.create.fields.sharedEmail'),
                                            variant: "default",
                                            "label-wrap": ""
                                        }, null, 8 /* PROPS */, ["modelValue", "label"])
                                    ], 2 /* CLASS */))
                                    : _createCommentVNode("v-if", true),
                                (_unref(form).contactType === 'Individual')
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                        _createElementVNode("div", _hoisted_12, [
                                            _createVNode(_unref(SelectInput), {
                                                modelValue: _unref(variantIndividual).category,
                                                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((_unref(variantIndividual).category) = $event)),
                                                label: _ctx.$t('contact.create.fields.category.label'),
                                                placeholder: _ctx.$t('contact.create.fields.category.placeholder'),
                                                options: categoryOptions.value,
                                                "full-width": ""
                                            }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "options"]),
                                            _createVNode(_unref(SelectInput), {
                                                modelValue: _unref(variantIndividual).source,
                                                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((_unref(variantIndividual).source) = $event)),
                                                label: _ctx.$t('contact.create.fields.source.label'),
                                                placeholder: _ctx.$t('contact.create.fields.source.placeholder'),
                                                options: sourceOptions.value,
                                                "full-width": ""
                                            }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "options"])
                                        ]),
                                        _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                            modelValue: _unref(address),
                                            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => (_isRef(address) ? (address).value = $event : null)),
                                            label: _ctx.$t('contact.create.fields.address'),
                                            options: _unref(addressSuggestions),
                                            loading: _unref(addressLoading),
                                            "show-add-more-details": showAddressDetailsLink.value,
                                            "add-more-details-label": _ctx.$t('contact.detail.addresses.formModal.address.addDetails'),
                                            "icon-left": "ri-map-pin-2-line",
                                            "full-width": "",
                                            "keep-input-on-edit": "",
                                            onSearch: _unref(onAddressSearch),
                                            onSelect: _unref(onAddressSelect),
                                            onAddMoreDetails: _unref(openAddressDetailsModal)
                                        }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "options", "loading", "show-add-more-details", "add-more-details-label", "onSearch", "onSelect", "onAddMoreDetails"]),
                                        _createVNode(_unref(PhoneInput), {
                                            modelValue: _unref(form).mainPhone,
                                            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => ((_unref(form).mainPhone) = $event)),
                                            label: _ctx.$t('contact.create.fields.phone'),
                                            "country-list-label": _ctx.$t('common.country'),
                                            locale: _unref(locale),
                                            error: _unref(phoneError),
                                            "disable-internal-validation": true,
                                            "full-width": "",
                                            onValidationChange: _unref(onPhoneValidationChange)
                                        }, null, 8 /* PROPS */, ["modelValue", "label", "country-list-label", "locale", "error", "onValidationChange"])
                                    ], 64 /* STABLE_FRAGMENT */))
                                    : (_unref(form).contactType === 'Professional')
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                            _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                                modelValue: _unref(variantPro).organisationId,
                                                "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event) => ((_unref(variantPro).organisationId) = $event)),
                                                label: _ctx.$t('contact.create.fields.organisation.label'),
                                                placeholder: _ctx.$t('contact.create.fields.organisation.placeholder'),
                                                "empty-label": _ctx.$t('contact.create.fields.organisation.empty'),
                                                options: lockedOrgOption.value ?? _unref(organisationSuggestions),
                                                loading: _unref(organisationsLoading),
                                                error: _unref(organisationIdError),
                                                required: true,
                                                disabled: !!props.initialOrganisationId,
                                                debounce: 250,
                                                "icon-left": "ri-building-line",
                                                "icon-right": "ri-search-line",
                                                "full-width": "",
                                                "create-label": props.initialOrganisationId ? undefined : _ctx.$t('common.createNewOrganisation'),
                                                onSearch: _cache[11] || (_cache[11] = ($event) => (!props.initialOrganisationId && onOrgSearch($event))),
                                                onClear: _cache[12] || (_cache[12] = ($event) => (!props.initialOrganisationId && _unref(clearOrgSearch)())),
                                                onBlur: _unref(onOrganisationBlur),
                                                onCreate: openCreateOrg
                                            }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "empty-label", "options", "loading", "error", "disabled", "create-label", "onBlur"]),
                                            _createElementVNode("div", _hoisted_13, [
                                                _createVNode(_unref(TextInput), {
                                                    modelValue: _unref(variantPro).position,
                                                    "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event) => ((_unref(variantPro).position) = $event)),
                                                    label: _ctx.$t('contact.create.fields.position'),
                                                    placeholder: _ctx.$t('contact.create.fields.positionPlaceholder'),
                                                    error: _unref(positionError),
                                                    required: true,
                                                    maxlength: 100,
                                                    "full-width": "",
                                                    onBlur: _unref(onPositionBlur)
                                                }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error", "onBlur"]),
                                                _createVNode(_unref(PhoneInput), {
                                                    modelValue: _unref(form).mainPhone,
                                                    "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event) => ((_unref(form).mainPhone) = $event)),
                                                    label: _ctx.$t('contact.create.fields.phone'),
                                                    "country-list-label": _ctx.$t('common.country'),
                                                    locale: _unref(locale),
                                                    error: _unref(phoneError),
                                                    "disable-internal-validation": true,
                                                    "full-width": "",
                                                    onValidationChange: _unref(onPhoneValidationChange)
                                                }, null, 8 /* PROPS */, ["modelValue", "label", "country-list-label", "locale", "error", "onValidationChange"])
                                            ])
                                        ], 64 /* STABLE_FRAGMENT */))
                                        : _createCommentVNode("v-if", true),
                                (serverError.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                        _cache[22] || (_cache[22] = _createElementVNode("i", { class: "ri-error-warning-line text-base shrink-0" }, null, -1 /* CACHED */)),
                                        _createTextVNode(" " + _toDisplayString(serverError.value), 1 /* TEXT */)
                                    ]))
                                    : _createCommentVNode("v-if", true)
                            ]),
                            _createElementVNode("div", _hoisted_15, [
                                _createElementVNode("div", _hoisted_16, [
                                    _createVNode(ContactDedupPanel, {
                                        state: effectiveDedupState.value,
                                        results: effectiveDedupResults.value,
                                        title: effectiveDedupTitle.value,
                                        hint: effectiveDedupHint.value,
                                        "max-results": _unref(DEDUP_PANEL_MAX),
                                        onPreview: handlePreview,
                                        onGoTo: _cache[15] || (_cache[15] = (id) => _unref(router).push({ name: 'ContactDetail', params: { id } }))
                                    }, null, 8 /* PROPS */, ["state", "results", "title", "hint", "max-results"])
                                ])
                            ])
                        ])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "close-on-escape", "aria-labelledby", "aria-describedby"]),
                _createVNode(ContactPreviewDrawer, {
                    modelValue: _unref(showPreviewDrawer),
                    "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event) => (_isRef(showPreviewDrawer) ? (showPreviewDrawer).value = $event : null)),
                    contact: _unref(previewContact),
                    "fallback-title": previewName.value,
                    loading: _unref(previewLoading),
                    error: _unref(previewError),
                    onGoTo: handlePreviewGoTo,
                    onRetry: _unref(retryPreview)
                }, null, 8 /* PROPS */, ["modelValue", "contact", "fallback-title", "loading", "error", "onRetry"]),
                _createVNode(AddressDetailsModal, {
                    modelValue: _unref(showAddressDetailsModal),
                    "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event) => (_isRef(showAddressDetailsModal) ? (showAddressDetailsModal).value = $event : null)),
                    "initial-details": _unref(addressDetails),
                    onSave: _unref(saveAddressDetails)
                }, null, 8 /* PROPS */, ["modelValue", "initial-details", "onSave"]),
                (createOrgMounted.value)
                    ? (_openBlock(), _createBlock(CreateOrganisationModal, {
                        key: 0,
                        modelValue: showCreateOrg.value,
                        "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event) => ((showCreateOrg).value = $event)),
                        "redirect-on-create": false,
                        "initial-name": lastOrgQuery.value,
                        onCreated: onOrgCreated
                    }, null, 8 /* PROPS */, ["modelValue", "initial-name"]))
                    : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
